import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect } from 'react';

import { cn } from '@/utils/cn';

export type ImageLightboxLabels = {
  dialogLabel: string;
  close: string;
  previous: string;
  next: string;
  count: string;
  imageAlt: string;
};

type ImageLightboxProps = {
  images: string[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  labels: ImageLightboxLabels;
  caption?: string | null;
};

/**
 * Full-screen image viewer — same interaction model as Weive CRM public catalog lightbox:
 * dark backdrop, top toolbar with count and close, circular side nav, wrap-around arrows.
 */
export const ImageLightbox = ({
  images,
  index,
  onClose,
  onIndexChange,
  labels,
  caption,
}: ImageLightboxProps) => {
  const open = index != null && images.length > 0;
  const safeIndex = index == null ? 0 : Math.min(Math.max(index, 0), images.length - 1);
  const src = open ? images[safeIndex] : null;
  const hasMultiple = images.length > 1;

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    onIndexChange(safeIndex <= 0 ? images.length - 1 : safeIndex - 1);
  }, [hasMultiple, images.length, onIndexChange, safeIndex]);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    onIndexChange(safeIndex >= images.length - 1 ? 0 : safeIndex + 1);
  }, [hasMultiple, images.length, onIndexChange, safeIndex]);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, goPrev, goNext]);

  if (!open || !src) return null;

  const controlClassName = cn(
    'rounded-lg border border-white/35 bg-slate-900/55 text-white',
    'transition-colors hover:bg-slate-800/90',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/88 px-3 pb-6 pt-14 backdrop-blur-sm sm:px-4"
      role="dialog"
      aria-modal="true"
      aria-label={labels.dialogLabel}
      onClick={onClose}
    >
      <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 px-4 py-3 text-white">
        {hasMultiple ? (
          <span className="text-body-sm font-medium">{labels.count}</span>
        ) : (
          <span className="text-body-sm" aria-hidden />
        )}
        <button
          type="button"
          className={cn(controlClassName, 'px-3.5 py-2 text-body-sm font-semibold')}
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          {labels.close}
        </button>
      </div>

      {hasMultiple ? (
        <button
          type="button"
          className={cn(
            controlClassName,
            'absolute start-3 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center sm:start-5',
          )}
          aria-label={labels.previous}
          onClick={(event) => {
            event.stopPropagation();
            goPrev();
          }}
        >
          <ChevronLeft className="size-6" aria-hidden />
        </button>
      ) : null}

      <div
        className="flex max-h-full max-w-[min(100%,75rem)] flex-col items-center justify-center gap-3"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={src}
          alt={labels.imageAlt}
          className="max-h-[calc(100dvh-7.5rem)] w-auto max-w-full rounded-lg object-contain shadow-[0_20px_50px_rgb(0_0_0/0.45)]"
        />
        {caption ? (
          <p className="max-w-[min(100%,75rem)] px-2 text-center text-body-sm text-white/90">
            {caption}
          </p>
        ) : null}
      </div>

      {hasMultiple ? (
        <button
          type="button"
          className={cn(
            controlClassName,
            'absolute end-3 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center sm:end-5',
          )}
          aria-label={labels.next}
          onClick={(event) => {
            event.stopPropagation();
            goNext();
          }}
        >
          <ChevronRight className="size-6" aria-hidden />
        </button>
      ) : null}
    </div>
  );
};
